import { Report } from '../types/reporting';

interface NotificationConfig {
  priority: 'low' | 'medium' | 'high' | 'urgent';
  channels: ('console' | 'email')[];
  recipients: string[];
}

// Simple notification rules - in production, this would be more sophisticated
const NOTIFICATION_RULES: Record<string, NotificationConfig> = {
  'harassment': { 
    priority: 'high', 
    channels: ['console', 'email'], 
    recipients: ['admin@thedailydo.com'] 
  },
  'hate_speech': { 
    priority: 'high', 
    channels: ['console', 'email'], 
    recipients: ['admin@thedailydo.com'] 
  },
  'violence_threats': { 
    priority: 'urgent', 
    channels: ['console', 'email'], 
    recipients: ['admin@thedailydo.com'] 
  },
  'dangerous_event': { 
    priority: 'urgent', 
    channels: ['console', 'email'], 
    recipients: ['admin@thedailydo.com'] 
  },
  'doxxing': { 
    priority: 'urgent', 
    channels: ['console', 'email'], 
    recipients: ['admin@thedailydo.com'] 
  },
  'spam': { 
    priority: 'medium', 
    channels: ['console'], 
    recipients: ['moderator@thedailydo.com'] 
  },
  'fake_event': { 
    priority: 'medium', 
    channels: ['console'], 
    recipients: ['moderator@thedailydo.com'] 
  },
  'fake_profile': { 
    priority: 'medium', 
    channels: ['console'], 
    recipients: ['moderator@thedailydo.com'] 
  },
  'default': { 
    priority: 'low', 
    channels: ['console'], 
    recipients: ['moderator@thedailydo.com'] 
  }
};

class NotificationService {
  async sendModerationAlert(report: Report): Promise<void> {
    try {
      const config = NOTIFICATION_RULES[report.category] || NOTIFICATION_RULES.default;
      
      // Console notification (for development)
      if (config.channels.includes('console')) {
        this.logConsoleAlert(report, config);
      }

      // Email notification (placeholder - would integrate with email service)
      if (config.channels.includes('email')) {
        await this.sendEmailAlert(report, config);
      }

    } catch (error) {
      console.error('Error sending moderation alert:', error);
      // Don't throw - notifications shouldn't break the main flow
    }
  }

  private logConsoleAlert(report: Report, config: NotificationConfig): void {
    const priorityEmoji = {
      'urgent': '🚨',
      'high': '⚠️',
      'medium': '🔔',
      'low': 'ℹ️'
    };

    console.log(`
${priorityEmoji[config.priority]} MODERATION ALERT [${config.priority.toUpperCase()}]
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📋 Report ID: ${report.id}
🎯 Target: ${report.targetTitle} (${report.type})
📝 Category: ${report.category.replace('_', ' ').toUpperCase()}
💬 Reason: ${report.reason}
👤 Reporter: ${report.reporterName}
🕐 Time: ${report.createdAt.toDate().toLocaleString()}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🔗 Review: /admin/moderation
    `);
  }

  private async sendEmailAlert(report: Report, config: NotificationConfig): Promise<void> {
    // Placeholder for email integration
    // In production, this would integrate with services like:
    // - SendGrid
    // - Amazon SES
    // - Mailgun
    // - Postmark
    
    console.log(`📧 EMAIL ALERT: Would send to ${config.recipients.join(', ')}`);
    console.log(`Subject: [${config.priority.toUpperCase()}] New ${report.category} report`);
    
    // Example email content structure:
    const emailContent = {
      to: config.recipients,
      subject: `[${config.priority.toUpperCase()}] New ${report.category.replace('_', ' ')} report`,
      template: 'moderation_alert',
      data: {
        reportId: report.id,
        category: report.category,
        targetType: report.type,
        targetTitle: report.targetTitle,
        reason: report.reason,
        reporterName: report.reporterName,
        timestamp: report.createdAt.toDate().toLocaleString(),
        reviewUrl: `${process.env.REACT_APP_BASE_URL}/admin/moderation`,
        priority: config.priority
      }
    };

    // TODO: Integrate with actual email service
    console.log('Email would be sent with data:', emailContent);
  }

  // Method for immediate escalation alerts
  async sendEscalationAlert(report: Report, reason: string): Promise<void> {
    const escalationMessage = `
🔥 ESCALATION ALERT
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📋 Report ID: ${report.id}
⏰ Escalation Reason: ${reason}
🎯 Target: ${report.targetTitle} (${report.type})
📝 Category: ${report.category}
🕐 Time: ${new Date().toLocaleString()}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    `;

    console.warn(escalationMessage);
    
    // In production, this would also send urgent notifications via:
    // - Slack webhooks
    // - SMS alerts
    // - Push notifications to admin apps
  }

  // Method for sending updates to users
  async notifyReportStatus(report: Report, newStatus: string, resolution?: string): Promise<void> {
    // In production, this would notify the reporter about status changes
    console.log(`📢 REPORT UPDATE: Report ${report.id} status changed to ${newStatus}`);
    
    if (resolution) {
      console.log(`📝 Resolution: ${resolution}`);
    }

    // TODO: Implement user notifications via:
    // - In-app notifications
    // - Email updates
    // - Push notifications
  }

  // Method for community safety alerts
  async sendSafetyAlert(message: string, severity: 'info' | 'warning' | 'critical'): Promise<void> {
    const severityEmoji = {
      'info': 'ℹ️',
      'warning': '⚠️',
      'critical': '🚨'
    };

    console.log(`${severityEmoji[severity]} SAFETY ALERT [${severity.toUpperCase()}]: ${message}`);
    
    // In production, this could broadcast to:
    // - All online moderators
    // - Community managers
    // - Safety team
  }

  // Health check method for monitoring notification system
  async healthCheck(): Promise<{ status: 'ok' | 'error', services: Record<string, boolean> }> {
    const services = {
      console: true, // Console is always available
      email: false, // Would check email service connectivity
      slack: false, // Would check Slack webhook connectivity
      sms: false,   // Would check SMS service connectivity
    };

    const allServicesWorking = Object.values(services).some(status => status);
    
    return {
      status: allServicesWorking ? 'ok' : 'error',
      services
    };
  }
}

export const notificationService = new NotificationService();